import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, Download, Check, AlertTriangle, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

// Etat d'un critere : "ok" (vert), "mid" (partiel ou repris), "ko" (rouge).
const COULEUR = {
  ok: "bg-green-100 text-green-800",
  mid: "bg-amber-100 text-amber-900",
  ko: "bg-red-100 text-red-800",
};

function criteres(e) {
  return [
    ["Photo camion", e.photo === "fiche" || e.photo === "galerie" ? "ok"
      : e.photo === "site" ? "mid" : "ko",
     e.photo === "site" ? "photo du site" : e.photo ? "oui" : "manquante"],
    ["Photos plats", e.plats >= 4 ? "ok" : e.plats ? "mid" : "ko", `${e.plats}/4`],
    ["Carte", e.carte ? "ok" : "ko", e.carte ? `${e.carte} plats` : "vide"],
    ["Texte", e.texte === "fiche" ? "ok" : e.texte === "auto" ? "mid" : "ko",
     e.texte === "fiche" ? "oui" : e.texte === "auto" ? "automatique" : "manquant"],
    ["Dimensions", e.dimensions ? "ok" : "ko", e.dimensions ? "oui" : "manquantes"],
    ["Électricité", e.electricite === "detail" ? "ok"
      : e.electricite === "ancien" ? "mid" : "ko",
     e.electricite === "detail" ? "détaillée"
      : e.electricite === "ancien" ? `ancien : ${e.ancien_electricite}` : "manquante"],
  ];
}

function Pastilles({ etat }) {
  if (!etat) return null;
  return (
    <span className="flex flex-wrap gap-1 mt-1">
      {criteres(etat).map(([lib, c, txt]) => (
        <span key={lib} title={`${lib} : ${txt}`}
              className={`text-[11px] rounded px-1.5 py-0.5 ${COULEUR[c]}`}>
          {lib}{c !== "ok" ? ` : ${txt}` : ""}
        </span>
      ))}
    </span>
  );
}

// Dossier de presentation de l'association : tous les camions actifs ou
// une selection. Independant des appels d'offres.
export default function DossierPresentationPage() {
  const [camions, setCamions] = useState([]);
  const [choisis, setChoisis] = useState([]);
  const [filtre, setFiltre] = useState("");
  const [infos, setInfos] = useState({
    client: "", evenement: "", date_evenement: "", lieu: "", convives: "",
  });
  const [formule, setFormule] = useState("");
  const [minimum, setMinimum] = useState(false);
  const [montant, setMontant] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [charge, setCharge] = useState(false);
  const [tableau, setTableau] = useState(false);
  const [aConfirmer, setAConfirmer] = useState(false);

  useEffect(() => {
    axios.get(`${API}/presentation/camions`)
      .then((r) => {
        const l = r.data.camions || [];
        setCamions(l);
        setChoisis(l.map((c) => c.id));
      })
      .catch(() => toast.error("Liste des camions indisponible"))
      .finally(() => setCharge(true));
  }, []);

  const visibles = useMemo(() => camions.filter((c) =>
    !filtre || `${c.nom} ${c.specialite}`.toLowerCase()
      .includes(filtre.toLowerCase())), [camions, filtre]);

  const basculer = (id) => setChoisis((x) =>
    x.includes(id) ? x.filter((i) => i !== id) : [...x, id]);
  const tous = () => setChoisis(camions.map((c) => c.id));
  const aucun = () => setChoisis([]);
  const champ = (k, v) => setInfos((x) => ({ ...x, [k]: v }));

  const incomplets = camions.filter((c) => choisis.includes(c.id) && !c.pret);

  const demanderGeneration = () => {
    if (!formule) return toast.error("Choisissez qui paie les repas.");
    if (!choisis.length) return toast.error("Sélectionnez au moins un camion.");
    if (incomplets.length) {
      setAConfirmer(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    generer();
  };

  const generer = async () => {
    setAConfirmer(false);
    if (!formule) return toast.error("Choisissez qui paie les repas.");
    if (!choisis.length) return toast.error("Sélectionnez au moins un camion.");
    const m = montant === "" ? null : parseFloat(String(montant).replace(",", "."));
    if (minimum && m !== null && (isNaN(m) || m < 0 || m > 100000))
      return toast.error("Minimum garanti : un montant entre 0 et 100 000 € HT.");
    setEnCours(true);
    try {
      // Ordre de la liste affichee, pour un dossier previsible.
      const ordre = camions.map((c) => c.id).filter((id) => choisis.includes(id));
      const corps = {
        ...infos,
        convives: infos.convives ? parseInt(infos.convives, 10) || null : null,
        camions: ordre.length === camions.length ? [] : ordre,
        formule,
        minimum_garanti: minimum,
        minimum_montant: minimum ? m : null,
      };
      const r = await axios.post(`${API}/presentation/pdf`, corps,
                                 { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const lien = document.createElement("a");
      lien.href = url;
      const nom = (infos.client || "food-truck-united")
        .replace(/[^a-z0-9]+/gi, "-");
      lien.download = `presentation-${nom}.pdf`;
      lien.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier de présentation généré");
    } catch (e) {
      let msg = "Génération impossible";
      try {
        const t = JSON.parse(await e?.response?.data?.text());
        if (t?.detail) msg = t.detail;
      } catch { /* reponse non JSON */ }
      toast.error(msg);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" /> Dossier de présentation
          </h1>
          <p className="text-sm text-muted-foreground">
            Présentation de l'association et de ses food trucks, à envoyer à
            un client ou un organisateur. Choisissez les camions : le dossier
            se remplit avec leurs fiches.
          </p>
        </div>
        <Button onClick={demanderGeneration} disabled={enCours || !choisis.length || !formule}>
          <Download className="h-4 w-4 mr-1" />
          {enCours ? "Génération…" : "Générer le dossier"}
        </Button>
      </div>

      {aConfirmer && (
        <Card className="border-amber-400 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              {incomplets.length} camion{incomplets.length > 1 ? "s" : ""} sélectionné
              {incomplets.length > 1 ? "s ont" : " a"} une fiche incomplète
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-xs text-amber-900">
              Le dossier sera généré avec ce qui existe : photos du site, texte
              automatique, emplacements vides ou mentions « à préciser ».
            </p>
            <ul className="space-y-1 max-h-72 overflow-auto">
              {incomplets.map((c) => (
                <li key={c.id}>
                  <span className="font-medium">{c.nom}</span>
                  <span className="text-amber-900"> : {c.manque.join(", ")}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={generer}>Générer quand même</Button>
              <Button size="sm" variant="outline" onClick={() => setAConfirmer(false)}>
                Revenir à la sélection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={formule ? "" : "border-amber-300"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Formule : qui paie les repas ?</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["prepaye", "Prépayé par l'organisateur",
             "Il offre le repas à ses convives : tickets ou bracelets, pas d'encaissement sur place, une facture unique."],
            ["convives", "Les convives paient leur repas",
             "Chacun règle son repas au camion de son choix : aucun encaissement à gérer pour l'organisateur."],
            ["les_deux", "Les deux, au choix du client",
             "Le dossier présente les deux formules côte à côte ; le client choisit."]]
            .map(([code, titre, aide]) => (
            <label key={code}
                   className={`border rounded-md p-3 cursor-pointer flex gap-2 items-start ${
                     formule === code ? "border-primary bg-primary/5" : "hover:bg-muted/40"}`}>
              <input type="radio" name="formule" className="mt-1"
                     checked={formule === code}
                     onChange={() => setFormule(code)} />
              <span className="text-sm">
                <span className="font-medium block">{titre}</span>
                <span className="text-xs text-muted-foreground">{aide}</span>
              </span>
            </label>
          ))}
          <div className="md:col-span-3 border-t pt-3 flex flex-wrap items-end gap-4">
            <fieldset className="space-y-1">
              <legend className="text-sm font-medium">Minimum garanti</legend>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="minimum" checked={!minimum}
                         onChange={() => setMinimum(false)} /> Non
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="minimum" checked={minimum}
                         onChange={() => setMinimum(true)} /> Oui
                </label>
              </div>
            </fieldset>
            {minimum && (
              <div className="space-y-1">
                <label htmlFor="pres-minimum" className="text-xs text-muted-foreground">
                  Montant par camion (€ HT, facultatif)
                </label>
                <Input id="pres-minimum" className="w-44" inputMode="decimal"
                       placeholder="ex. 800" value={montant}
                       onChange={(e) => setMontant(e.target.value)} />
              </div>
            )}
            <p className="text-xs text-muted-foreground basis-full">
              {minimum
                ? (formule === "prepaye"
                    ? "Le dossier indiquera un montant minimum facturé par camion, quel que soit le nombre de repas."
                    : formule === "les_deux"
                      ? "Le dossier précisera le minimum pour chacune des deux formules."
                      : "Le dossier indiquera que l'organisateur complète les ventes d'un camion jusqu'au minimum.")
                : (formule === "prepaye" || formule === "les_deux" ? "Pas de mention de minimum dans le dossier."
                    : "Le dossier précisera qu'aucun montant n'est dû aux camions si les ventes sont faibles.")}
            </p>
          </div>
          {!formule && (
            <p className="text-xs text-amber-800 md:col-span-3">
              À choisir avant de générer : les textes du dossier changent selon la formule.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pour qui ? (facultatif)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {[["client", "Destinataire", "ex. HEC Paris"],
            ["evenement", "Événement", "ex. Soirée des Alumni"],
            ["date_evenement", "Date", ""],
            ["lieu", "Lieu", "ex. Campus de Jouy-en-Josas"],
            ["convives", "Nombre de convives", "ex. 600"]].map(([k, lib, ph]) => (
            <div key={k} className="space-y-1">
              <label htmlFor={`pres-${k}`} className="text-xs text-muted-foreground">
                {lib}
              </label>
              <Input id={`pres-${k}`} placeholder={ph}
                     type={k === "date_evenement" ? "date"
                       : k === "convives" ? "number" : "text"}
                     min={k === "convives" ? 1 : undefined}
                     value={infos[k]}
                     onChange={(e) => champ(k, e.target.value)} />
            </div>
          ))}
          <p className="text-xs text-muted-foreground md:col-span-2">
            Sans destinataire ni événement, le dossier devient une présentation
            générale de l'association.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
          <CardTitle className="text-base">
            Food trucks : {choisis.length} sur {camions.length}
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setTableau((t) => !t)}>
              {tableau ? "Masquer le tableau" : "Tableau de ce qui manque"}
            </Button>
            <Button variant="outline" size="sm" onClick={tous}>Tous</Button>
            <Button variant="outline" size="sm" onClick={aucun}>Aucun</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input className="pl-8" placeholder="Rechercher un camion"
                   aria-label="Rechercher un camion"
                   value={filtre} onChange={(e) => setFiltre(e.target.value)} />
          </div>
          {tableau && (
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2">Camion</th>
                    {["Photo camion", "Photos plats", "Carte", "Texte", "Dimensions", "Électricité"]
                      .map((h) => <th key={h} className="p-2">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {camions.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="p-2 font-medium whitespace-nowrap">{c.nom}</td>
                      {(c.etat ? criteres(c.etat) : []).map(([lib, col, txt]) => (
                        <td key={lib} className={`p-2 text-center ${COULEUR[col]}`}>
                          {col === "ok" ? "✔" : txt}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground p-2">
                Vert : présent. Orange : partiel ou repris automatiquement. Rouge : manquant.
                Chaque adhérent complète sa fiche dans « Ma fiche camion ».
              </p>
            </div>
          )}
          {!charge && <p className="text-sm text-muted-foreground">Chargement…</p>}
          {charge && !camions.length && (
            <p className="text-sm text-muted-foreground">Aucun camion actif.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {visibles.map((c) => (
              <label key={c.id}
                     className="flex items-start gap-2 border rounded-md p-2 cursor-pointer hover:bg-muted/40">
                <input type="checkbox" className="mt-1"
                       checked={choisis.includes(c.id)}
                       onChange={() => basculer(c.id)} />
                <span className="text-sm min-w-0">
                  <span className="font-medium">{c.nom}</span>
                  {c.specialite && (
                    <span className="text-muted-foreground"> · {c.specialite.slice(0, 40)}</span>
                  )}
                  {c.pret ? (
                    <span className="block text-xs mt-0.5 text-green-700 inline-flex items-center gap-1">
                      <Check className="h-3 w-3" /> Fiche complète
                    </span>
                  ) : c.etat ? (
                    <Pastilles etat={c.etat} />
                  ) : (
                    <span className="block text-xs mt-0.5 text-amber-700">
                      manque : {c.manque.join(", ")}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
          {incomplets.length > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 rounded-md p-2">
              {incomplets.length} camion{incomplets.length > 1 ? "s" : ""} sélectionné
              {incomplets.length > 1 ? "s ont" : " a"} une fiche incomplète : le dossier
              affichera des emplacements vides ou « à préciser ».
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
